import "./clinical-navigation-enhancements.css";

export interface QuickXrayOrder {
  catalogId: string;
  bodyRegion: string;
  displayName: string;
  shorthand: string;
}

export function deriveFrequentXrayQuickOrders(
  catalogButtons: readonly HTMLElement[],
  limit = 8,
): QuickXrayOrder[] {
  const seenRegions = new Set<string>();
  const quickOrders: QuickXrayOrder[] = [];

  for (const button of catalogButtons) {
    const catalogId = button.dataset.catalogId?.trim() ?? "";
    const bodyRegion = button.querySelector<HTMLElement>(":scope > span")?.textContent?.trim() ?? "";
    const displayName = button.querySelector<HTMLElement>(":scope > strong")?.textContent?.trim() ?? "";
    const shorthand = button.querySelector<HTMLElement>(":scope > small b")?.textContent?.trim() ?? "";
    if (!catalogId || !bodyRegion || !displayName || seenRegions.has(bodyRegion)) continue;

    seenRegions.add(bodyRegion);
    quickOrders.push({ catalogId, bodyRegion, displayName, shorthand });
    if (quickOrders.length >= limit) break;
  }

  return quickOrders;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[character] ?? character,
  );
}

function focusSelectedPatientChart(documentRef: Document): void {
  const selectedQueueCard = documentRef.querySelector<HTMLElement>(".queue-card.selected");
  const chart = documentRef.querySelector<HTMLElement>(".note-card, .clinical-editor-card, #clinical-editor");
  if (!chart) return;

  chart.setAttribute("tabindex", "-1");
  chart.focus({ preventScroll: true });
  chart.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });

  const patientName = selectedQueueCard?.querySelector("strong")?.textContent?.trim();
  const announcer = documentRef.querySelector<HTMLElement>("#patient-chart-load-announcer");
  if (announcer) {
    announcer.textContent = patientName
      ? `${patientName} 환자의 진료차트를 불러왔습니다.`
      : "선택한 환자의 진료차트를 불러왔습니다.";
  }
}

function renderQuickXrayOrders(documentRef: Document): void {
  const orderCard = documentRef.querySelector<HTMLElement>(".orders-card");
  if (!orderCard) return;

  const existing = orderCard.querySelector<HTMLElement>("[data-quick-xray-orders]");
  const xrayTab = orderCard.querySelector<HTMLElement>(
    '[data-action="order-modality"][data-modality="xray"]',
  );
  const searchInput = orderCard.querySelector<HTMLInputElement>("#order-search");
  const xrayActive = xrayTab?.classList.contains("active") === true;
  const searching = Boolean(searchInput?.value.trim());

  if (!xrayActive || searching) {
    existing?.remove();
    return;
  }

  const catalogButtons = Array.from(
    orderCard.querySelectorAll<HTMLElement>(
      '.order-catalog [data-action="add-order"][data-catalog-id]',
    ),
  );
  const quickOrders = deriveFrequentXrayQuickOrders(catalogButtons);
  if (quickOrders.length === 0) {
    existing?.remove();
    return;
  }

  const quickOrderMarkup = `<section class="quick-xray-orders" data-quick-xray-orders aria-label="정형외과 다빈도 X-ray 빠른 오더">
    <div class="quick-xray-heading">
      <div><span>ORTHOPEDIC QUICK X-RAY</span><strong>부위별 다빈도 바로 오더</strong></div>
      <small>클릭 시 초안만 생성 · 의사 확정 전 전송 안 됨</small>
    </div>
    <div class="quick-xray-grid">
      ${quickOrders
        .map(
          (order) => `<button type="button" class="quick-xray-button"
            data-quick-catalog-id="${escapeHtml(order.catalogId)}">
            <span>${escapeHtml(order.bodyRegion)}</span>
            <strong>${escapeHtml(order.displayName)}</strong>
            <small>${escapeHtml(order.shorthand)}</small>
          </button>`,
        )
        .join("")}
    </div>
  </section>`;

  if (existing) {
    existing.outerHTML = quickOrderMarkup;
    return;
  }

  const searchRow = orderCard.querySelector<HTMLElement>(".order-search-row");
  searchRow?.insertAdjacentHTML("beforebegin", quickOrderMarkup);
}

export function mountClinicalNavigationEnhancements(
  documentRef: Document = document,
  windowRef: Window = window,
): () => void {
  const root = documentRef.querySelector<HTMLElement>("#app");
  if (!root) throw new Error("App root not found for clinical navigation enhancements.");

  if (!documentRef.querySelector("#patient-chart-load-announcer")) {
    const announcer = documentRef.createElement("div");
    announcer.id = "patient-chart-load-announcer";
    announcer.className = "sr-only";
    announcer.setAttribute("role", "status");
    announcer.setAttribute("aria-live", "polite");
    documentRef.body.append(announcer);
  }

  let renderScheduled = false;
  const scheduleEnhancementRender = (): void => {
    if (renderScheduled) return;
    renderScheduled = true;
    windowRef.requestAnimationFrame(() => {
      renderScheduled = false;
      renderQuickXrayOrders(documentRef);
    });
  };

  const observer = new MutationObserver(scheduleEnhancementRender);
  observer.observe(root, { childList: true, subtree: true });

  const clickListener = (event: Event): void => {
    const target = (event.target as Element | null)?.closest<HTMLElement>(
      ".queue-card[data-action='select'], [data-quick-catalog-id]",
    );
    if (!target) return;

    const quickCatalogId = target.dataset.quickCatalogId;
    if (quickCatalogId) {
      const originalButton = Array.from(
        root.querySelectorAll<HTMLElement>(
          '.order-catalog [data-action="add-order"][data-catalog-id]',
        ),
      ).find((button) => button.dataset.catalogId === quickCatalogId);
      originalButton?.click();
      return;
    }

    if (target.matches(".queue-card[data-action='select']")) {
      windowRef.requestAnimationFrame(() => {
        windowRef.requestAnimationFrame(() => focusSelectedPatientChart(documentRef));
      });
    }
  };

  root.addEventListener("click", clickListener);
  scheduleEnhancementRender();

  return () => {
    observer.disconnect();
    root.removeEventListener("click", clickListener);
    documentRef.querySelector("#patient-chart-load-announcer")?.remove();
  };
}
